// src/firebase.js — FCM + App Check only (Firestore/Storage migrated to Supabase)
//
// This file no longer exports `db` or `storage`. Use `src/supabase.js` instead.
// Exports: messaging, appId, appCheck bypass logic, getTenantPathSegments, getTenantPath

import { initializeApp } from 'firebase/app';
import { getMessaging } from 'firebase/messaging';
import { initializeAppCheck, CustomProvider } from 'firebase/app-check';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);

const APP_CHECK_CACHE_KEY = 'adapthub_app_check_token';

const appCheckCustomProvider = new CustomProvider({
  getToken: () => {
    // Check sessionStorage for a still-valid cached token
    try {
      const cached = sessionStorage.getItem(APP_CHECK_CACHE_KEY);
      if (cached) {
        const { token, expireTimeMillis } = JSON.parse(cached);
        if (token && expireTimeMillis && Date.now() < expireTimeMillis) {
          return Promise.resolve({ token, expireTimeMillis });
        }
      }
    } catch { /* ignore cache errors */ }

    return new Promise((resolve, reject) => {
      // 90 second absolute safety timeout to give the user enough time to complete an interactive challenge
      const timeoutId = setTimeout(() => {
        console.warn("[APP CHECK] Turnstile token acquisition timed out. Rejecting.");
        cleanup();
        reject(new Error("Turnstile token acquisition timed out."));
      }, 90000);

      const div = document.createElement('div');
      div.id = 'turnstile-wrapper';
      // Place widget in bottom-right corner so it does NOT overlap the centered login button.
      // If a challenge is triggered, user can still see and click it in the corner.
      // Using bottom-right because login button is centered — avoids click interception.
      // pointerEvents: none on wrapper lets clicks pass through to page elements;
      // child iframe gets pointer-events: auto via injected style so Turnstile can capture
      // interaction-only clicks when needed.
      div.style.position = 'fixed';
      div.style.top = '50%';
      div.style.left = '50%';
      div.style.transform = 'translate(-50%, -50%)';
      div.style.width = '300px';
      div.style.height = '65px';
      div.style.zIndex = '99999';
      div.style.pointerEvents = 'none';
      document.body.appendChild(div);

      // Inject style to ensure all Turnstile widget elements remain clickable despite parent pointer-events: none
      const style = document.createElement('style');
      style.innerHTML = '#turnstile-wrapper * { pointer-events: auto !important; }';
      document.head.appendChild(style);

      let widgetId: string | null | undefined = null;
      let intervalId: ReturnType<typeof setInterval> | null = null;

      const cleanup = () => {
        clearTimeout(timeoutId);
        if (intervalId) clearInterval(intervalId);
        if (widgetId && window.turnstile) {
          try { window.turnstile.remove(widgetId); } catch { /* ignore */ }
        }
        if (style.parentNode) {
          try { style.parentNode.removeChild(style); } catch { /* ignore */ }
        }
        // Defer wrapper div removal to allow Turnstile's async remove/reset logic to complete safely
        const targetDiv = div;
        setTimeout(() => {
          if (targetDiv.parentNode) {
            try { targetDiv.parentNode.removeChild(targetDiv); } catch { /* ignore */ }
          }
        }, 100);
      };

      const renderTurnstile = () => {
        try {
          // renderTurnstile is only ever invoked after the script loaded, so
          // this guard never fires — it just narrows the optional global.
          const turnstile = window.turnstile;
          if (!turnstile) {
            cleanup();
            reject(new Error("Turnstile script not loaded."));
            return;
          }
          const sitekey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
          if (!sitekey) {
            console.warn("[APP CHECK] VITE_TURNSTILE_SITE_KEY is missing. Rejecting.");
            cleanup();
            reject(new Error("VITE_TURNSTILE_SITE_KEY is missing."));
            return;
          }

          widgetId = turnstile.render(div, {
            sitekey: sitekey,
            retry: 'never',
            appearance: 'interaction-only',
            callback: async (turnstileToken) => {
              try {
                const response = await fetch('/api/services?action=turnstile', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    token: turnstileToken,
                    appId: import.meta.env.VITE_FIREBASE_APP_ID
                  })
                });
                const data = await response.json();
                if (data.token) {
                  cleanup();
                  // Cache the validated token in sessionStorage
                  try {
                    sessionStorage.setItem(APP_CHECK_CACHE_KEY, JSON.stringify({
                      token: data.token,
                      expireTimeMillis: data.expireTimeMillis
                    }));
                  } catch { /* ignore cache errors */ }
                  resolve({
                    token: data.token,
                    expireTimeMillis: data.expireTimeMillis
                  });
                } else {
                  console.warn("[APP CHECK] Turnstile verification failed on backend:", data.error);
                  cleanup();
                  reject(new Error(data.error || "Turnstile verification failed."));
                }
              } catch (e) {
                console.error("[APP CHECK] Turnstile token verification request failed:", e);
                cleanup();
                reject(e);
              }
            },
            'error-callback': () => {
              console.warn("[APP CHECK] Turnstile verification challenge error.");
              cleanup();
              reject(new Error("Turnstile verification challenge error."));
            }
          });
        } catch (renderError) {
          console.error("[APP CHECK] Exception during Turnstile render:", renderError);
          cleanup();
          reject(renderError);
        }
      };

      if (window.turnstile) {
        renderTurnstile();
      } else {
        let attempts = 0;
        intervalId = setInterval(() => {
          attempts++;
          if (window.turnstile) {
            if (intervalId) clearInterval(intervalId);
            renderTurnstile();
          } else if (attempts >= 50) { // 5 seconds maximum waiting for script
            console.warn("[APP CHECK] Turnstile script was not loaded (possibly blocked by privacy/ad-block extension). Rejecting.");
            cleanup();
            reject(new Error("Turnstile script not loaded."));
          }
        }, 100);
      }
    });
  }
});

const isLocalhost =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.startsWith('192.168.') ||
  window.location.hostname.startsWith('10.') ||
  /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(window.location.hostname);

// Skip App Check on Vercel preview environments
const isVercelPreview =
  window.location.hostname.endsWith('.vercel.app') &&
  (window.location.hostname.includes('-git-') || window.location.hostname !== 'adapt-hub.vercel.app');

const bypassAppCheck = isLocalhost || isVercelPreview;

if (!bypassAppCheck) {
  initializeAppCheck(app, {
    provider: appCheckCustomProvider,
    isTokenAutoRefreshEnabled: true
  });
} else {
  console.log(`App Check skipped on ${isVercelPreview ? 'Vercel preview' : 'local development'} environment.`);
}

// getMessaging() throws synchronously ("messaging/unsupported-browser") on
// browsers lacking the required APIs — Sentry issue ADAPTHUB-B (141 events).
// Guard here so module import is safe on every browser; consumers (e.g.
// usePushNotifications) already treat a null messaging as "push disabled".
// NOTE: firebase's isSupported() is async (Promise<boolean>) so it cannot be
// awaited at module scope; the sync try/catch is the practical guard because
// getMessagingInWindow throws synchronously once isWindowSupported() rejects.
export const messaging = (() => {
  if (typeof window === 'undefined') return null;
  try {
    return getMessaging(app);
  } catch (error) {
    console.warn('[firebase] push messaging unsupported in this browser:', error);
    return null;
  }
})();
export const appId = import.meta.env.VITE_APP_ID || 'apaleo-all-in-one-dev';

// ── Supabase migration compat re-exports ──
// Keeps the build green for callers still importing path helpers from "./firebase".
export { getTenantPathSegments, getTenantPath } from './supabase';
